import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { projectSurfaceStyle } from "./projectColors";
import { projectTitle } from "./projectIdentity";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { useState } from "react";
import { useStore } from "ra-core";
import { useReturnToPrevious } from "../misc/useReturnToPrevious";
import { ProjectFinance } from "../finance/ProjectFinance";
import { DeferredProjectQuote } from "../specifications/DeferredProjectQuote";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Archive, ArchiveRestore } from "lucide-react";
import {
  CanAccess,
  Navigate,
  InfiniteListBase,
  ShowBase,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { DeleteButton } from "@/components/admin/delete-button";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceArrayField } from "@/components/admin/reference-array-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { NoteCreate } from "../notes/NoteCreate";
import { NotesIterator } from "../notes/NotesIterator";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { ContactList } from "./ContactList";
import { findDealLabel } from "./dealUtils";
import { formatProjectDate, isProjectOverdue } from "./projectUtils";
import { ProjectTasks } from "./ProjectTasks";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useDealPipelines } from "./useDealPipelines";

export const DealShow = () => {
  const handleClose = useReturnToPrevious("/deals");
  const [quoteDirty, setQuoteDirty] = useState(false);
  return (
    <CanAccess
      resource="deals"
      action="show"
      accessDenied={<Navigate to="/access-denied" />}
    >
      <ShowBase resource="deals">
        <main
          className="mx-auto w-full min-w-0 max-w-[1440px] space-y-4 px-3 pb-24 pt-3 sm:px-0 sm:pb-8"
          aria-label="Карточка проекта"
        >
          <Button
            variant="ghost"
            onClick={() => {
              if (
                !quoteDirty ||
                window.confirm(
                  "В КП есть несохранённые изменения. Выйти без сохранения?",
                )
              )
                handleClose();
            }}
          >
            <ArrowLeft className="size-4" />
            Назад
          </Button>
          <DealShowContent onQuoteDirtyChange={setQuoteDirty} />
        </main>
      </ShowBase>
    </CanAccess>
  );
};

const DealShowContent = ({
  onQuoteDirtyChange,
}: {
  onQuoteDirtyChange: (dirty: boolean) => void;
}) => {
  const { data: identity } = useGetIdentity();
  const { getDealPipeline, dealPipelines } = useDealPipelines();
  const { dealCategories, currency } = useConfigurationContext();
  const record = useRecordContext<Deal>();
  const [tab, setTab] = useStore<string>(
    `rovero.deal.${record?.id ?? "none"}.tab`,
    "overview",
  );
  const [overviewSections, setOverviewSections] = useState<string[]>([
    tab === "tasks" || tab === "notes" ? tab : "details",
  ]);
  const [visitedSections, setVisitedSections] = useState<string[]>([
    tab === "tasks" || tab === "notes" ? tab : "details",
  ]);
  const [visitedTabs, setVisitedTabs] = useState<string[]>([tab]);
  const selectTab = (next: string) => {
    setVisitedTabs((current) => [...new Set([...current, next])]);
    setTab(next);
  };
  if (!record) return null;
  const activeTab =
    (tab === "quote" && !identity?.administrator) ||
    ["tasks", "notes"].includes(tab)
      ? "overview"
      : tab;
  const pipeline = getDealPipeline(record.pipeline);
  return (
    <div className="space-y-6 sm:space-y-0 sm:flex sm:min-h-0 sm:flex-1 sm:flex-col sm:gap-4">
      {record.archived_at && <ArchivedTitle />}
      <header
        className="space-y-3 shrink-0 rounded-2xl border p-4"
        style={projectSurfaceStyle(record.calendar_color)}
        data-project-color={record.calendar_color}
      >
        <p className="text-sm text-muted-foreground">
          Проект ROVERO · {pipeline.label}
        </p>
        <h1 className="text-lg sm:text-2xl break-words">
          {projectTitle(record)}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">
            {findDealLabel(pipeline.stages, record.stage) ?? record.stage}
          </Badge>
          {isProjectOverdue(record, new Date(), dealPipelines) && (
            <Badge variant="destructive">Срок просрочен</Badge>
          )}
          {record.archived_at ? (
            <>
              <UnarchiveButton record={record} />
              <DeleteButton />
            </>
          ) : (
            <>
              <EditButton />
              <ArchiveButton record={record} />
            </>
          )}
        </div>
        <div className="grid shrink-0 grid-cols-2 gap-3 border-t pt-3 sm:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">Стоимость КП</p>
            <p className="font-medium">
              {record.amount == null
                ? "Ещё не согласовано"
                : record.amount.toLocaleString("ru-RU", {
                    style: "currency",
                    currency,
                    maximumFractionDigits: 2,
                  })}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              Плановый срок завершения
            </p>
            <p className="font-medium">
              {formatProjectDate(record.expected_closing_date)}
            </p>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className="text-xs text-muted-foreground">
              Ответственный за проект
            </p>
            <ReferenceField
              source="sales_id"
              reference="sales"
              link={false}
              empty="Не назначен"
            />
          </div>
        </div>
      </header>
      <Tabs
        value={activeTab}
        onValueChange={selectTab}
        className="sm:min-h-0 sm:flex-1"
      >
        <div className="sticky top-0 z-10 shrink-0 rounded-full bg-background">
          <SegmentedControl
            label="Разделы сделки"
            value={activeTab}
            onValueChange={selectTab}
            className="w-full [&_.rovero-segment>span]:px-1 [&_.rovero-segment>span]:text-xs sm:[&_.rovero-segment>span]:px-3 sm:[&_.rovero-segment>span]:text-sm"
            options={[
              { value: "overview", label: "Обзор" },
              ...(identity?.administrator
                ? [{ value: "quote", label: "КП" }]
                : []),
              { value: "finance", label: "Финансы" },
            ]}
          />
        </div>
        <TabsContent
          aria-label="Обзор проекта"
          forceMount
          hidden={activeTab !== "overview"}
          value="overview"
          className="space-y-5 pt-4 "
        >
          <Accordion
            type="multiple"
            value={overviewSections}
            onValueChange={(next) => {
              setOverviewSections(next);
              setVisitedSections((current) => [
                ...new Set([...current, ...next]),
              ]);
            }}
            className="space-y-3"
          >
            <AccordionItem value="details" variant="card">
              <AccordionTrigger>
                <span>
                  Информация о проекте
                  <span className="mt-1 block text-xs font-normal text-muted-foreground">
                    Клиент, описание и требования
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent
                forceMount
                hidden={!overviewSections.includes("details")}
                className="space-y-5"
              >
                <section className="space-y-3 rounded-xl border bg-card p-4">
                  <h3 className="font-medium">Клиент</h3>
                  {record.company_id != null && (
                    <div className="flex items-center gap-2">
                      <ReferenceField
                        source="company_id"
                        reference="companies"
                        link="show"
                      >
                        <CompanyAvatar />
                      </ReferenceField>
                      <ReferenceField
                        source="company_id"
                        reference="companies"
                        link="show"
                      />
                    </div>
                  )}
                  {record.contact_ids?.length ? (
                    <ReferenceArrayField
                      source="contact_ids"
                      reference="contacts_summary"
                    >
                      <ContactList />
                    </ReferenceArrayField>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Контакты не указаны
                    </p>
                  )}
                </section>
                {record.category && (
                  <section>
                    <h3 className="font-medium">Тип работ / изделия</h3>
                    <p className="text-sm">
                      {dealCategories.find((c) => c.value === record.category)
                        ?.label ?? record.category}
                    </p>
                  </section>
                )}
                <section>
                  <h3 className="font-medium mb-2">Описание и требования</h3>
                  <p className="text-sm whitespace-pre-line">
                    {record.description || "Описание пока не добавлено"}
                  </p>
                </section>
                <Separator />
                <p className="text-xs text-muted-foreground">
                  Создан: {formatProjectDate(record.created_at)} · Обновлён:{" "}
                  {formatProjectDate(record.updated_at)}
                </p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="tasks" variant="card">
              <AccordionTrigger>
                <span>
                  Задачи
                  <span className="mt-1 block text-xs font-normal text-muted-foreground">
                    Поручения и контроль выполнения
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent
                forceMount
                hidden={!overviewSections.includes("tasks")}
              >
                {visitedSections.includes("tasks") && (
                  <ProjectTasks project={record} />
                )}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="notes" variant="card">
              <AccordionTrigger>
                <span>
                  Заметки и файлы
                  <span className="mt-1 block text-xs font-normal text-muted-foreground">
                    Обсуждение, документы и вложения
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent
                forceMount
                hidden={!overviewSections.includes("notes")}
              >
                {visitedSections.includes("notes") && (
                  <InfiniteListBase
                    resource="deal_notes"
                    filter={{ deal_id: record.id }}
                    sort={{ field: "date", order: "DESC" }}
                    perPage={25}
                    disableSyncWithLocation
                    storeKey={false}
                    empty={<NoteCreate reference="deals" />}
                  >
                    <NotesIterator reference="deals" />
                  </InfiniteListBase>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
        <TabsContent
          aria-label="Финансы"
          value="finance"
          forceMount
          hidden={activeTab !== "finance"}
          className="pt-4 "
        >
          {visitedTabs.includes("finance") && (
            <ProjectFinance project={record} />
          )}
        </TabsContent>
        {identity?.administrator && (
          <TabsContent
            aria-label="Спецификация и КП"
            value="quote"
            forceMount
            hidden={activeTab !== "quote"}
            className="pt-4  data-[state=inactive]:hidden"
          >
            <DeferredProjectQuote
              project={record}
              active={activeTab === "quote"}
              permitted={!!identity?.administrator}
              onDirtyChange={onQuoteDirtyChange}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

const ArchivedTitle = () => {
  const translate = useTranslate();
  return (
    <div className="bg-orange-500 px-6 py-4">
      <h3 className="text-lg font-bold text-[#FBF8F2]">
        {translate("resources.deals.archived.title")}
      </h3>
    </div>
  );
};

const ArchiveButton = ({ record }: { record: Deal }) => {
  const returnToPrevious = useReturnToPrevious("/deals");
  const translate = useTranslate();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleClick = () => {
    update(
      "deals",
      {
        id: record.id,
        data: { archived_at: new Date().toISOString() },
        previousData: record,
      },
      {
        onSuccess: () => {
          returnToPrevious();
          notify("resources.deals.archived.success", {
            type: "info",
            undoable: false,
          });
          refresh();
        },
        onError: () => {
          notify("resources.deals.archived.error", {
            type: "error",
          });
        },
      },
    );
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <Archive className="w-4 h-4" />
      {translate("resources.deals.archived.action")}
    </Button>
  );
};

const UnarchiveButton = ({ record }: { record: Deal }) => {
  const returnToPrevious = useReturnToPrevious("/deals");
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate } = useMutation({
    mutationFn: () => dataProvider.unarchiveDeal(record),
    onSuccess: () => {
      returnToPrevious();
      notify("resources.deals.unarchived.success", {
        type: "info",
        undoable: false,
      });
      refresh();
    },
    onError: () => {
      notify("resources.deals.unarchived.error", {
        type: "error",
      });
    },
  });

  const handleClick = () => {
    mutate();
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <ArchiveRestore className="w-4 h-4" />
      {translate("resources.deals.unarchived.action")}
    </Button>
  );
};
